import { useRef, type ReactNode } from "react";
import { cn } from "../cn";
import { useT } from "../../i18n";

export type CellKey = string; // "x,y" in world coordinates

export function keyOf(x: number, y: number): CellKey {
  return `${x},${y}`;
}

/**
 * SellMiniGrid — interactive parcel mini-grid for partial-sale area selection.
 * Click toggles a single cell; click-and-drag paints (or erases) a rectangle.
 * Cells already locked by an existing listing are shown and non-selectable.
 * Presentation-only: the backend re-validates every selection authoritatively.
 */
export function SellMiniGrid({
  parcel,
  selected,
  onChange,
  lockedCells,
  disabled = false,
  testId = "sell-mini-grid",
}: {
  parcel: { x: number; y: number; w: number; h: number };
  selected: Set<CellKey>;
  onChange: (next: Set<CellKey>) => void;
  lockedCells?: Set<CellKey>;
  disabled?: boolean;
  testId?: string;
}) {
  const t = useT();
  const dragging = useRef(false);
  const mode = useRef<"add" | "remove">("add");
  const base = useRef<Set<CellKey>>(new Set());
  const start = useRef<{ x: number; y: number } | null>(null);

  const { x: px, y: py, w, h } = parcel;

  const applyRect = (cx: number, cy: number) => {
    if (!start.current) return;
    const x0 = Math.min(start.current.x, cx);
    const x1 = Math.max(start.current.x, cx);
    const y0 = Math.min(start.current.y, cy);
    const y1 = Math.max(start.current.y, cy);
    const next = new Set(base.current);
    for (let gy = y0; gy <= y1; gy++) {
      for (let gx = x0; gx <= x1; gx++) {
        const k = keyOf(gx, gy);
        if (lockedCells?.has(k)) continue;
        if (mode.current === "add") next.add(k);
        else next.delete(k);
      }
    }
    onChange(next);
  };

  const onDown = (gx: number, gy: number) => {
    if (disabled) return;
    const k = keyOf(gx, gy);
    if (lockedCells?.has(k)) return;
    dragging.current = true;
    mode.current = selected.has(k) ? "remove" : "add";
    base.current = new Set(selected);
    start.current = { x: gx, y: gy };
    applyRect(gx, gy);
  };
  const onEnter = (gx: number, gy: number) => {
    if (dragging.current) applyRect(gx, gy);
  };
  const endDrag = () => {
    dragging.current = false;
    start.current = null;
  };

  const nodes: ReactNode[] = [];
  for (let gy = py; gy < py + h; gy++) {
    for (let gx = px; gx < px + w; gx++) {
      const k = keyOf(gx, gy);
      const isSel = selected.has(k);
      const isLocked = lockedCells?.has(k) ?? false;
      nodes.push(
        <button
          key={k}
          type="button"
          data-testid={`${testId}-cell-${gx}-${gy}`}
          data-selected={isSel ? "true" : "false"}
          data-locked={isLocked ? "true" : "false"}
          disabled={isLocked || disabled}
          onPointerDown={(e) => {
            e.preventDefault();
            onDown(gx, gy);
          }}
          onPointerEnter={() => onEnter(gx, gy)}
          className={cn(
            "aspect-square rounded-[2px] border transition-[background-color,border-color] duration-fast",
            isLocked
              ? "bg-neon-coral/20 border-border-coral cursor-not-allowed"
              : isSel
                ? "bg-neon-teal/70 border-neon-teal shadow-glow-teal-sm"
                : "bg-elevated/50 border-border-divider hover:border-neon-teal/60 hover:bg-elevated",
          )}
          aria-label={isLocked ? t("marketplace.grid.cellLockedAria", { x: gx, y: gy }) : t("marketplace.grid.cellAria", { x: gx, y: gy })}
        />,
      );
    }
  }

  return (
    <div
      data-testid={testId}
      onPointerUp={endDrag}
      onPointerLeave={endDrag}
      className="grid gap-[2px] select-none touch-none mx-auto w-full"
      style={{
        gridTemplateColumns: `repeat(${w}, minmax(0, 1fr))`,
        maxWidth: Math.min(360, Math.max(120, w * 34)),
      }}
    >
      {nodes}
    </div>
  );
}
